/**
 * WorkbenchActionBar Component
 * Unified action bar for all workbench states:
 * - SetOps results (union, intersection, etc.)
 * - Pending manual changes (drag/drop, edits)
 *
 * Replaces the need for separate "Save Changes" buttons throughout the UI
 */

import { useState, useEffect } from 'react';
import { Save, FilePlus, X, Clock, GitMerge, Loader2, AlertCircle, Filter, Play } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSetOpsStore } from '../../stores/setops';
import { useWorkbenchStore } from '../../stores/workbench';
import { useSaveWorkbench, useSaveStatus } from '../../hooks/useSaveWorkbench';
import { playlistKeys } from '../../api/hooks/usePlaylists';
import { getApiClient } from '../../api/client';
import type { PlaylistDetail } from '../../api/types';

/**
 * Human-readable labels for set operations
 */
const OPERATION_LABELS: Record<string, string> = {
  union: 'Union (A ∪ B)',
  intersection: 'Intersection (A ∩ B)',
  difference: 'Difference (A \\ B)',
  symmetric_difference: 'Symmetric Diff (A △ B)',
};

/**
 * Unified action bar for workbench operations
 * Shows appropriate actions based on current state
 */
export function WorkbenchActionBar() {
  const { resultMetadata, resultTracks, clearResult } = useSetOpsStore();
  const {
    activePlaylist,
    tracks,
    markClean,
    filterPreviewMode,
    filterPreviewMetadata,
    clearFilterPreview,
    restoreOriginalOrder,
    capabilities,
  } = useWorkbenchStore();
  const { hasChanges, canSave } = useSaveStatus();
  const saveMutation = useSaveWorkbench();
  const queryClient = useQueryClient();
  const [isSavingNew, setIsSavingNew] = useState(false);
  const [isCommittingFilter, setIsCommittingFilter] = useState(false);

  // Priority 1: SetOps result takes precedence
  const hasSetOpsResult = !!(resultMetadata && resultTracks);

  // Priority 2: Filter preview mode
  const hasFilterPreview = filterPreviewMode && filterPreviewMetadata;

  // Priority 3: Manual changes (drag/drop, edits) - only if playlist supports saving
  const hasPendingChanges = hasChanges && !hasSetOpsResult && !hasFilterPreview && capabilities.canSave;

  const api = getApiClient();

  /**
   * Global CTRL-S keyboard shortcut for saving
   * Must be before early return to satisfy Rules of Hooks
   */
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Check for CTRL-S (or CMD-S on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's default save dialog

        // Priority 1: Save SetOps result if present
        if (hasSetOpsResult && !saveMutation.isPending && activePlaylist && resultTracks) {
          try {
            const currentPlaylist = await api.get<PlaylistDetail>(`/playlists/${activePlaylist.id}`);
            const currentUris = currentPlaylist.tracks.items.map((item) => item.track.uri);
            if (currentUris.length > 0) {
              await api.delete(`/playlists/${activePlaylist.id}/tracks`, { uris: currentUris });
            }
            await api.post(`/playlists/${activePlaylist.id}/tracks`, {
              uris: resultTracks.map((t) => t.uri),
            });
            markClean();
            clearResult();
            queryClient.invalidateQueries({ queryKey: playlistKeys.all });
          } catch (error) {
            console.error('Failed to save setops result:', error);
          }
          return;
        }

        // Priority 2: Commit filter preview if active
        if (hasFilterPreview && !isCommittingFilter && activePlaylist && filterPreviewMetadata) {
          setIsCommittingFilter(true);
          try {
            const { workbenchService } = await import('../../api/services/workbench');
            await workbenchService.applyFilter(activePlaylist.id, {
              ...filterPreviewMetadata.criteria,
              action: filterPreviewMetadata.action,
              newPlaylistName: filterPreviewMetadata.newPlaylistName,
            });
            clearFilterPreview();
            markClean();
            queryClient.invalidateQueries({ queryKey: playlistKeys.all });
          } catch (error) {
            console.error('Failed to commit filter:', error);
          } finally {
            setIsCommittingFilter(false);
          }
          return;
        }

        // Priority 3: Save pending changes if any
        if (hasPendingChanges && canSave && !saveMutation.isPending && activePlaylist) {
          saveMutation.mutate({
            playlistId: activePlaylist.id,
            tracks,
          });
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    hasSetOpsResult,
    hasFilterPreview,
    hasPendingChanges,
    canSave,
    saveMutation,
    isCommittingFilter,
    activePlaylist,
    resultTracks,
    filterPreviewMetadata,
    tracks,
    api,
    markClean,
    clearResult,
    clearFilterPreview,
    queryClient,
    setIsCommittingFilter,
  ]);

  // Don't render if no actions needed
  if (!hasSetOpsResult && !hasPendingChanges && !hasFilterPreview) {
    return null;
  }

  /**
   * Save SetOps result to current playlist
   * IMPORTANT: Must replace ALL tracks, not append
   */
  const handleSaveSetOpsResult = async () => {
    if (!activePlaylist || !resultTracks) return;

    try {
      // STEP 1: Remove all existing tracks from the playlist
      // Get all current track URIs from Spotify
      const currentPlaylist = await api.get<PlaylistDetail>(`/playlists/${activePlaylist.id}`);
      const currentUris = currentPlaylist.tracks.items.map((item) => item.track.uri);

      if (currentUris.length > 0) {
        await api.delete(`/playlists/${activePlaylist.id}/tracks`, {
          uris: currentUris,
        });
      }

      // STEP 2: Add result tracks to the now-empty playlist
      await api.post(`/playlists/${activePlaylist.id}/tracks`, {
        uris: resultTracks.map((t) => t.uri),
      });

      markClean();
      clearResult();

      // Invalidate playlists list to update track count in sidebar
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    } catch (error) {
      console.error('Failed to save setops result:', error);
    }
  };

  /**
   * Save manual changes to current playlist
   */
  const handleSavePendingChanges = () => {
    if (!canSave || !activePlaylist) return;
    saveMutation.mutate({
      playlistId: activePlaylist.id,
      tracks,
    });
  };

  /**
   * Commit filter preview to Spotify backend
   */
  const handleCommitFilter = async () => {
    if (!activePlaylist || !filterPreviewMetadata) return;

    setIsCommittingFilter(true);
    try {
      // Import workbenchService dynamically to avoid circular dependency
      const { workbenchService } = await import('../../api/services/workbench');

      await workbenchService.applyFilter(activePlaylist.id, {
        ...filterPreviewMetadata.criteria,
        action: filterPreviewMetadata.action,
        newPlaylistName: filterPreviewMetadata.newPlaylistName,
      });

      // Clear preview mode and mark as clean
      clearFilterPreview();
      markClean();

      // Invalidate playlists list to update track count
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    } catch (error) {
      console.error('Failed to commit filter:', error);
    } finally {
      setIsCommittingFilter(false);
    }
  };

  /**
   * Save SetOps result as new playlist
   */
  const handleSaveAsNew = async () => {
    if (!resultMetadata || !resultTracks) return;

    setIsSavingNew(true);
    try {
      const name = `${resultMetadata.sourceAName} ${OPERATION_LABELS[resultMetadata.operation]} ${resultMetadata.sourceBName}`;

      await api.post('/playlists', {
        name,
        description: `Created via ${resultMetadata.operation} operation with ${resultMetadata.matchMode} matching`,
        track_uris: resultTracks.map((t) => t.uri),
      });

      clearResult();

      // Invalidate playlists list to show new playlist in sidebar
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    } catch (error) {
      console.error('Failed to create new playlist:', error);
    } finally {
      setIsSavingNew(false);
    }
  };

  // Render SetOps result bar
  if (hasSetOpsResult) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-3 bg-emerald-500/10 border-b border-emerald-500/30 backdrop-blur-md animate-slide-down shadow-lg">
        <div className="flex items-center justify-between">
          {/* Result Info */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-emerald-400">
              <GitMerge className="w-4 h-4" />
              <span className="font-medium">
                {OPERATION_LABELS[resultMetadata.operation]}
              </span>
            </div>

            <div className="text-sm text-zinc-400">
              {resultTracks.length} tracks
            </div>

            {resultMetadata.duplicatesFound > 0 && (
              <div className="text-xs text-zinc-500">
                {resultMetadata.isrcMatches} ISRC matches, {resultMetadata.fuzzyMatches} fuzzy matches
              </div>
            )}

            <div className="flex items-center gap-1 text-xs text-zinc-500">
              <Clock className="w-3 h-3" />
              {resultMetadata.executionTimeMs}ms
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveSetOpsResult}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>

            <button
              onClick={handleSaveAsNew}
              disabled={isSavingNew}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-700/50 disabled:cursor-not-allowed text-zinc-100 text-sm font-medium transition-colors"
            >
              {isSavingNew ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FilePlus className="w-4 h-4" />
                  Save as New
                </>
              )}
            </button>

            <button
              onClick={clearResult}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
              title="Discard result"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render filter preview bar
  if (hasFilterPreview) {
    const actionText =
      filterPreviewMetadata.action === 'remove_filtered'
        ? 'Remove Filtered'
        : 'Keep Only Filtered';
    const actionColor =
      filterPreviewMetadata.action === 'remove_filtered' ? 'rose' : 'blue';

    return (
      <div
        className={`fixed top-0 left-0 right-0 z-50 px-6 py-3 border-b backdrop-blur-md animate-slide-down shadow-lg ${
          actionColor === 'rose'
            ? 'bg-rose-500/10 border-rose-500/30'
            : 'bg-blue-500/10 border-blue-500/30'
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Preview Info */}
          <div className="flex items-center gap-4">
            <div
              className={`flex items-center gap-2 ${
                actionColor === 'rose' ? 'text-rose-400' : 'text-blue-400'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="font-medium">Filter Preview: {actionText}</span>
            </div>

            <div className="text-sm text-zinc-400">
              {tracks.length} tracks {actionColor === 'rose' ? 'remaining' : 'matched'}
            </div>

            <div className="text-xs text-zinc-500">
              Preview active - changes not saved
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleCommitFilter}
              disabled={isCommittingFilter}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg disabled:cursor-not-allowed text-white text-sm font-medium transition-colors ${
                actionColor === 'rose'
                  ? 'bg-rose-500 hover:bg-rose-600 disabled:bg-rose-500/50'
                  : 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50'
              }`}
            >
              {isCommittingFilter ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Apply to Spotify
                </>
              )}
            </button>

            <button
              onClick={clearFilterPreview}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-100 text-sm font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Discard Preview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render pending changes bar
  if (hasPendingChanges) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-3 bg-blue-500/10 border-b border-blue-500/30 backdrop-blur-md animate-slide-down shadow-lg">
        <div className="flex items-center justify-between">
          {/* Pending Changes Info */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-blue-400">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Pending Changes</span>
            </div>

            <div className="text-sm text-zinc-400">
              Unsaved modifications to playlist
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSavePendingChanges}
              disabled={!canSave || saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>

            <button
              onClick={restoreOriginalOrder}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-700/50 disabled:cursor-not-allowed text-zinc-100 text-sm font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Discard Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
